// FragmentList — admin view of all fragments, with filter chips and
// retract action. Mirrors MessageList; visually distinct by surfacing
// the first photo thumbnail and a「· N 圖 / · 影 片」mono badge.

import {
  Hairline,
  Han,
  HUD,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { fragmentDoc } from "@goodbye/firebase";
import { Timestamp, serverTimestamp, updateDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminSyncCtx } from "../context";

type ReleaseFilter = "sent" | "retracted";

function formatTs(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== "function") return "—";
  const d = ts.toDate();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}/${day} ${hh}:${mm}`;
}

export function FragmentList() {
  const navigate = useNavigate();
  const { fragments, users, loading } = useAdminSyncCtx();
  const [releaseFilter, setReleaseFilter] = useState<ReleaseFilter>("sent");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  // 撤回二段確認:第一次 arm、第二次才撤回。4s auto-revert。2026-05-30。
  const [confirmingRetract, setConfirmingRetract] = useState<string | null>(null);
  useEffect(() => {
    if (!confirmingRetract) return;
    const t = window.setTimeout(() => setConfirmingRetract(null), 4000);
    return () => window.clearTimeout(t);
  }, [confirmingRetract]);

  const nameByUserId = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.id, u.name);
    return m;
  }, [users]);

  const visible = useMemo(() => {
    return fragments.filter((f) => {
      if (releaseFilter === "sent") return f.retracted !== true;
      return f.retracted === true;
    });
  }, [fragments, releaseFilter]);

  function markBusy(id: string) {
    setBusy((prev) => new Set(prev).add(id));
  }
  function clearBusy(id: string) {
    setBusy((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }

  async function retract(id: string) {
    if (busy.has(id)) return;
    markBusy(id);
    try {
      await updateDoc(fragmentDoc(id), {
        retracted: true,
        retractedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("[FragmentList] retract failed", err);
    } finally {
      clearBusy(id);
    }
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>
            碎 片
          </Han>
          <TextBtn
            size={13}
            color={T.ink900}
            ls="0.12em"
            onClick={() => navigate("/fragments/new")}
            style={{
              padding: "8px 14px",
              border: `1px solid ${T.ink200}`,
              whiteSpace: "nowrap",
            }}
          >
            新 建
          </TextBtn>
        </div>
        <MonoLabel size={11} color={T.ink500}>
          {loading
            ? "讀取中…"
            : `共 ${fragments.length} 個 · 篩選後 ${visible.length} 個`}
        </MonoLabel>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "10px 32px",
          display: "flex",
          gap: 14,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        {(
          [
            ["sent", "已 發 出"],
            ["retracted", "已 撤 回"],
          ] as const
        ).map(([v, zh]) => {
          const active = v === releaseFilter;
          return (
            <button
              type="button"
              key={v}
              onClick={() => setReleaseFilter(v)}
              style={{
                fontFamily: T.han,
                fontSize: 12,
                letterSpacing: "0.18em",
                color: active ? T.ink900 : T.ink350,
                background: "transparent",
                border: "none",
                borderBottom: `1px solid ${active ? T.ink900 : "transparent"}`,
                padding: "2px 0",
                cursor: "pointer",
              }}
            >
              {zh}
            </button>
          );
        })}
      </div>
      <Hairline style={{ margin: "0 32px" }} />

      <div
        style={{
          padding: "12px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {visible.length === 0 && !loading && (
          <Han size={14} color={T.ink500} lh={1.9}>
            還沒有碎片。點「· 新 建」開始。
          </Han>
        )}
        {visible.map((f) => {
          const isBusy = busy.has(f.id);
          const photoCount = f.photoUrls?.length ?? 0;
          const hasVideo = !!f.videoUrl;
          const firstPhoto = f.photoUrls?.[0];
          // 系統自動發的「集滿揭曉」個人照(rewardFragmentId 產生
          // reward-{uid}-{rid}-{n})不可撤回 —— 誤按撤回會讓學員辛苦集
          // 滿換來的照片消失。用固定 id 前綴辨識。2026-05-30。
          const isAutoReward = f.id.startsWith("reward-");
          return (
            <div
              key={f.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                paddingBottom: 14,
                borderBottom: `1px solid ${T.ink100}`,
                opacity: f.retracted ? 0.4 : 1,
              }}
            >
              {/* Row 1: thumb + text/preview + timestamp */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                {firstPhoto && (
                  <img
                    src={firstPhoto}
                    alt=""
                    style={{
                      width: 48,
                      height: 48,
                      objectFit: "cover",
                      flexShrink: 0,
                      border: `1px solid ${T.ink100}`,
                    }}
                  />
                )}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {f.text ? (
                    <Han
                      size={15}
                      color={T.ink900}
                      lh={1.55}
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {f.text}
                    </Han>
                  ) : (
                    <Han size={15} color={T.ink350}>
                      (無文字)
                    </Han>
                  )}
                </div>
                <MonoData size={12} color={T.ink500} style={{ flexShrink: 0 }}>
                  {formatTs(f.createdAt)}
                </MonoData>
              </div>

              {/* Row 2: badges (audience + 圖 + 影片 + retracted) */}
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "baseline",
                  flexWrap: "wrap",
                }}
              >
                <MonoLabel size={11} color={T.ink500}>
                  {f.broadcast
                    ? "全 體"
                    : f.audienceUserIds.length === 0
                      ? "(無收件人)"
                      : `${f.audienceUserIds.length} 人 · ${f.audienceUserIds
                          .map((uid) => nameByUserId.get(uid) ?? uid)
                          .join(" · ")}`}
                </MonoLabel>
                {photoCount > 0 && (
                  <MonoLabel size={11} color={T.ink700}>
                    {photoCount === 1 ? "· 圖" : `· ${photoCount} 圖`}
                  </MonoLabel>
                )}
                {hasVideo && (
                  <MonoLabel size={11} color={T.ink700}>
                    · 影 片
                  </MonoLabel>
                )}
                {f.source === "pin" && !isAutoReward && (
                  <MonoLabel size={11} color={T.ink350}>
                    · 學員收集
                  </MonoLabel>
                )}
                {isAutoReward && (
                  <MonoLabel size={11} color={T.ink350}>
                    · 集滿揭曉(自動)
                  </MonoLabel>
                )}
                {f.retracted && (
                  <MonoLabel size={11} color={T.ink350}>
                    已 撤 回
                  </MonoLabel>
                )}
              </div>

              {/* Row 3: action button */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 4,
                  justifyContent: "flex-end",
                }}
              >
                {isAutoReward ? (
                  // 系統自動發的揭曉照片:不提供撤回/再送按鈕,只標示
                  // 「不可撤回」,避免工作員誤按弄不見學員的照片。
                  <MonoLabel size={11} color={T.ink350}>
                    · 不可撤回
                  </MonoLabel>
                ) : f.retracted ? (
                  <TextBtn
                    size={12}
                    color={T.ink900}
                    ls="0.12em"
                    framed
                    onClick={() => navigate(`/fragments/new?from=${f.id}`)}
                    style={{
                      padding: "8px 14px",
                      border: `1px solid ${T.ink200}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    再 次 送 出
                  </TextBtn>
                ) : (
                  <TextBtn
                    size={12}
                    color={confirmingRetract === f.id ? T.ink900 : T.ink500}
                    ls="0.12em"
                    framed
                    onClick={() => {
                      if (isBusy) return;
                      if (confirmingRetract === f.id) {
                        setConfirmingRetract(null);
                        retract(f.id);
                      } else {
                        setConfirmingRetract(f.id);
                      }
                    }}
                    disabled={isBusy}
                    style={{
                      padding: "8px 12px",
                      border: `1px solid ${
                        confirmingRetract === f.id ? T.ink900 : T.ink200
                      }`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isBusy
                      ? "處 理 中…"
                      : confirmingRetract === f.id
                        ? "確 定 撤 回?"
                        : "撤 回"}
                  </TextBtn>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
